import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api, { fileSrc } from '../utils/api';
import Modal from '../components/Modal';
import { apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import { LOGO_URL } from '../components/BillDesign';

const TYPES = ['kitchen', 'store', 'retail', 'online', 'damaged'];
// 'store' is presented as "Warehouse" in the UI (the backend enum stays 'store').
const TYPE_LABEL = { kitchen: 'Kitchen', store: 'Warehouse', retail: 'Retail', online: 'Online', damaged: 'Damaged' };
const empty = { type: 'retail', name: '', address: '', phone: '', gstin: '', fssai: '', shopName: '', isActive: true };

const LocationModal = ({ location, onClose, onSaved }) => {
  const [form, setForm] = useState(location ? { ...empty, ...location } : empty);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // The logo goes up after the location is saved: a new location has no id to upload to before then.
  const [logoFile, setLogoFile] = useState(null);
  const [dropLogo, setDropLogo] = useState(false);
  const pickedLogo = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : null), [logoFile]);
  useEffect(() => () => { if (pickedLogo) URL.revokeObjectURL(pickedLogo); }, [pickedLogo]);
  const ownLogo = pickedLogo || (!dropLogo && fileSrc(form.logoUrl));
  const onLogo = (e) => {
    const file = e.target.files[0]; e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return setError('Logo must be a PNG, JPG or WebP image');
    if (file.size > 2 * 1024 * 1024) return setError('Logo must be 2 MB or less');
    setError(''); setLogoFile(file); setDropLogo(false);
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      let { data } = location ? await api.put(`/locations/${location.id}`, form) : await api.post('/locations', form);
      const id = data.location.id;
      if (logoFile) {
        const fd = new FormData(); fd.append('logo', logoFile);
        ({ data } = await api.post(`/locations/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
      } else if (dropLogo && form.logoUrl) {
        ({ data } = await api.delete(`/locations/${id}/logo`));
      }
      toast.success('Saved'); onSaved(data.location); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title={location ? 'Edit Location' : 'Add Location'} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <form onSubmit={save}>
        <div className="row">
          <div className="field"><label>Type</label><select value={form.type} onChange={(e) => set('type', e.target.value)}>{TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}</select></div>
          <div className="field" style={{ flex: 2 }}><label>Name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
        </div>
        <div className="field"><label>Address</label><textarea rows={2} value={form.address || ''} onChange={(e) => set('address', e.target.value)} placeholder="Printed on this branch's bills" /></div>
        <div className="row">
          <div className="field"><label>Phone</label><input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} inputMode="tel" placeholder="Printed on bills" /></div>
          <div className="field"><label>GSTIN</label><input value={form.gstin || ''} onChange={(e) => set('gstin', e.target.value)} placeholder="If this branch registers separately" /></div>
        </div>
        <div className="field"><label>FSSAI licence</label>
          <input value={form.fssai || ''} onChange={(e) => set('fssai', e.target.value)} inputMode="numeric" placeholder="14-digit licence for this premises" />
        </div>
        <div className="field"><label>Shop name on bills</label>
          <input value={form.shopName || ''} onChange={(e) => set('shopName', e.target.value)} placeholder="Blank prints the name from the bill design" />
        </div>
        <div className="field"><label>Logo on bills</label>
          <div className="flex gap" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <img src={ownLogo || LOGO_URL} alt="" style={{ height: 56, maxWidth: 120, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4, padding: 2, background: '#fff' }} />
            <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
              {ownLogo ? 'Change' : 'Upload logo'}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} style={{ display: 'none' }} />
            </label>
            {ownLogo && <button type="button" className="btn btn-outline btn-sm" onClick={() => { setLogoFile(null); setDropLogo(true); }}>Use default logo</button>}
            <span className="muted small">{ownLogo ? "This branch's own logo" : 'Default logo from the bill design'} · PNG, JPG or WebP, up to 2 MB</span>
          </div>
        </div>
        <label className="flex gap" style={{ alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} /> Active</label>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

const TYPE_ICON = { kitchen: '🍳', store: '🏬', retail: '🏪', online: '🌐', damaged: '⚠️' };

const Locations = () => {
  const { can } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);

  const { reloadLocations } = useLocationFilter();
  const load = () => { setLoading(true); api.get('/locations?all=1').then(({ data }) => setLocations(data.locations || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(load, []);
  const onSaved = () => { load(); reloadLocations(); };

  return (
    <div>
      <div className="section-head">
        <span className="muted small">{locations.length} locations</span>
        {can('locations:write') && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add Location</button>}
      </div>
      {loading ? <div className="spinner" /> : (
        <>
          {TYPES.map((type) => {
            const items = locations.filter((l) => l.type === type);
            if (!items.length) return null;
            return (
              <div key={type} className="loc-bucket" style={{ marginBottom: '1.5rem' }}>
                <div className="nav-group-label" style={{ padding: '0 0 .5rem', fontSize: '.8rem' }}>{TYPE_ICON[type] || '📍'} {TYPE_LABEL[type] || type} <span className="muted">({items.length})</span></div>
                <div className="stores-grid">
                  {items.map((l) => (
                    <div key={l.id} className="store-card">
                      <div className="store-card-topline">
                        <span className="store-badge">{TYPE_LABEL[l.type] || l.type}</span>
                        <span className={`store-status ${l.isActive ? 'store-status--open' : 'store-status--closed'}`}>{l.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                      <h3>{TYPE_ICON[l.type] || '📍'} {l.name}</h3>
                      {(l.shopName || l.logoUrl) && (
                        <div className="store-detail">
                          {l.logoUrl && <img src={fileSrc(l.logoUrl)} alt="" style={{ height: 22, maxWidth: 60, objectFit: 'contain' }} />}
                          <span>Bills as <strong>{l.shopName || 'the bill design name'}</strong>{l.logoUrl ? ' with its own logo' : ''}</span>
                        </div>
                      )}
                      <div className="store-detail">
                        <span className="store-detail-icon">📍</span>
                        <span>
                          {l.address || <span className="muted">No address on file</span>}
                          {l.phone ? ` · ${l.phone}` : ''}
                          {l.gstin ? ` · GSTIN ${l.gstin}` : ''}
                          {l.fssai ? ` · FSSAI ${l.fssai}` : ''}
                        </span>
                      </div>
                      {can('locations:write') && (
                        <div className="store-card-actions">
                          <button className="btn btn-outline btn-sm" onClick={() => setEditing(l)}>Edit</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {locations.length === 0 && <div className="empty">No locations yet.</div>}
        </>
      )}
      {editing !== undefined && <LocationModal location={editing} onClose={() => setEditing(undefined)} onSaved={onSaved} />}
    </div>
  );
};

export default Locations;
